import { useEffect, useState } from "react"
import { Table } from "reactstrap"
import ActionButton from "Components/atoms/buttons/action-button"
import Chip from "Components/atoms/chip"
import EmptyState from "Components/organisms/empty-state"
import LoadingState from "Components/organisms/loading-state"
import { ExpenseBudget, ExpenseBudgetAlert, ExpenseBudgetAlertStatus, employeeName } from "models/expense-budget"
import { getExpenseBudgetAlerts, updateExpenseBudgetAlert } from "slices/finance/expense-budget/thunk"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatCurrency } from "utils/tools"

interface AlertsTabProps {
    budget: ExpenseBudget
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const TYPE_LABELS: Record<string, string> = {
    MonthOverPlan: "Month over plan",
    ForecastOverApproved: "Forecast over approved",
    InvoiceSpike: "Invoice spike",
    UnplannedVendor: "Unplanned vendor",
    RenewalDue: "Renewal due",
}

const STATUS_VARIANT: Record<ExpenseBudgetAlertStatus, "danger" | "success" | "secondary"> = {
    [ExpenseBudgetAlertStatus.Open]: "danger",
    [ExpenseBudgetAlertStatus.Acknowledged]: "success",
    [ExpenseBudgetAlertStatus.Dismissed]: "secondary",
}

// "Acknowledged" is shown as "Approved"; the stored status is unchanged.
const STATUS_LABELS: Record<ExpenseBudgetAlertStatus, string> = {
    [ExpenseBudgetAlertStatus.Open]: "Open",
    [ExpenseBudgetAlertStatus.Acknowledged]: "Approved",
    [ExpenseBudgetAlertStatus.Dismissed]: "Dismissed",
}

const periodLabel = (fiscalPeriod: number | null) =>
    fiscalPeriod ? calendar.fiscalToLabel(Math.floor(fiscalPeriod / 100), fiscalPeriod % 100) : "-"

// Alerts are re-evaluated by the API every time this tab loads.
const AlertsTab = ({ budget }: AlertsTabProps) => {
    const [alerts, setAlerts] = useState<ExpenseBudgetAlert[] | null>(null)

    const loadAlerts = async () => setAlerts(await getExpenseBudgetAlerts(budget.id))

    useEffect(() => { loadAlerts() }, [budget.id])

    const handleUpdate = async (alert: ExpenseBudgetAlert, status: ExpenseBudgetAlertStatus) => {
        const comment = window.prompt("Comment (optional)", alert.comment ?? "") ?? undefined
        if (await updateExpenseBudgetAlert(alert.id, status, comment)) await loadAlerts()
    }

    if (!alerts) return <LoadingState title="Loading Alerts" subtitle="Checking actuals against the plan..." />
    if (alerts.length === 0) return <EmptyState title="No alerts" subtitle="Actuals are within the plan thresholds." />

    return (
        <Table size="sm" hover className="align-middle">
            <thead>
                <tr>
                    <th>Status</th><th>Type</th><th>Period</th><th>Detail</th>
                    <th className="text-end">Plan</th><th className="text-end">Actual</th><th>Comment</th><th />
                </tr>
            </thead>
            <tbody>
                {alerts.map(alert => (
                    <tr key={alert.id}>
                        <td><Chip variant={STATUS_VARIANT[alert.status]}>{STATUS_LABELS[alert.status]}</Chip></td>
                        <td>{TYPE_LABELS[alert.type] ?? alert.type}</td>
                        <td>{periodLabel(alert.fiscalPeriod)}</td>
                        <td>{alert.message}</td>
                        <td className="text-end">{formatCurrency(alert.plannedAmount)}</td>
                        <td className="text-end">{formatCurrency(alert.actualAmount)}</td>
                        <td>
                            {alert.comment ?? "-"}
                            {alert.acknowledgedBy && <div className="text-muted small">{employeeName(alert.acknowledgedBy)}</div>}
                        </td>
                        <td>
                            <div className="d-flex gap-1 justify-content-end">
                                {alert.status === ExpenseBudgetAlertStatus.Open ? <>
                                    <ActionButton onClick={() => handleUpdate(alert, ExpenseBudgetAlertStatus.Acknowledged)}>Approve</ActionButton>
                                    <ActionButton onClick={() => handleUpdate(alert, ExpenseBudgetAlertStatus.Dismissed)}>Dismiss</ActionButton>
                                </> : <ActionButton onClick={() => handleUpdate(alert, ExpenseBudgetAlertStatus.Open)}>Reopen</ActionButton>}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

export default AlertsTab
